import { AccessibilityInfo } from 'react-native';
import { normalizeApiError } from '../api/apiError';

export type OperationKind = 'add' | 'update' | 'delete';
export type OperationOutcome = 'success' | 'failure';

export type OperationFeedback = {
  id: number;
  kind: OperationKind;
  outcome: OperationOutcome;
  title: string;
  /** Optional reason from the API, shown under the title on failure. */
  detail: string | null;
};

export const OPERATION_FEEDBACK_TITLES: Record<
  OperationKind,
  Record<OperationOutcome, string>
> = {
  add: { success: 'Added successfully', failure: 'Add failed' },
  update: { success: 'Updated successfully', failure: 'Update failed' },
  delete: { success: 'Deleted successfully', failure: 'Delete failed' },
};

const DISPLAY_MS: Record<OperationOutcome, number> = {
  success: 2500,
  failure: 4000,
};

export type OperationFeedbackSnapshot = {
  feedback: OperationFeedback | null;
  /** Only the most recently mounted host renders, so popups sit above open modals. */
  activeHostId: number | null;
};

let nextFeedbackId = 1;
let nextHostId = 1;
let hostStack: number[] = [];
let dismissTimer: ReturnType<typeof setTimeout> | null = null;
let snapshot: OperationFeedbackSnapshot = {
  feedback: null,
  activeHostId: null,
};
const listeners = new Set<() => void>();

function publish(feedback: OperationFeedback | null) {
  snapshot = {
    feedback,
    activeHostId: hostStack.length ? hostStack[hostStack.length - 1] : null,
  };
  listeners.forEach(listener => listener());
}

export function subscribeOperationFeedback(listener: () => void) {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function getOperationFeedbackSnapshot(): OperationFeedbackSnapshot {
  return snapshot;
}

export function registerOperationFeedbackHost(): {
  hostId: number;
  unregister: () => void;
} {
  const hostId = nextHostId++;
  hostStack = [...hostStack, hostId];
  publish(snapshot.feedback);
  return {
    hostId,
    unregister: () => {
      hostStack = hostStack.filter(id => id !== hostId);
      publish(snapshot.feedback);
    },
  };
}

export function dismissOperationFeedback() {
  if (dismissTimer) {
    clearTimeout(dismissTimer);
    dismissTimer = null;
  }
  if (snapshot.feedback) publish(null);
}

export function showOperationFeedback(
  kind: OperationKind,
  outcome: OperationOutcome,
  detail?: string | null,
): OperationFeedback {
  const feedback: OperationFeedback = {
    id: nextFeedbackId++,
    kind,
    outcome,
    title: OPERATION_FEEDBACK_TITLES[kind][outcome],
    detail: detail?.trim() ? detail.trim() : null,
  };
  if (dismissTimer) clearTimeout(dismissTimer);
  dismissTimer = setTimeout(() => {
    dismissTimer = null;
    if (snapshot.feedback?.id === feedback.id) publish(null);
  }, DISPLAY_MS[outcome]);
  publish(feedback);
  AccessibilityInfo.announceForAccessibility?.(
    feedback.detail ? `${feedback.title}. ${feedback.detail}` : feedback.title,
  );
  return feedback;
}

/**
 * Runs an add/update/delete API call and shows the matching popup once the
 * API has answered. Failures are re-thrown so screens keep their own error
 * handling (inline messages, retry state, etc.).
 */
export async function runOperation<T>(
  kind: OperationKind,
  action: () => Promise<T>,
  options: {
    failureDetail?: (caught: unknown) => string | null;
    /** Shown under the success title. Omit to keep the standard title only. */
    successDetail?: (result: T) => string | null;
  } = {},
): Promise<T> {
  let result: T;
  try {
    result = await action();
  } catch (caught) {
    const detail = options.failureDetail
      ? options.failureDetail(caught)
      : normalizeApiError(caught).message;
    showOperationFeedback(kind, 'failure', detail);
    throw caught;
  }
  showOperationFeedback(kind, 'success', options.successDetail?.(result));
  return result;
}
